/*** IMPORTS FROM imports-loader ***/
var define = false;

import AsyncValidator from 'async-validator'
import get from 'lodash/get'
import uuid from 'uuid'
import emitter from '../../mixin/emitter'

const noop = function doNothing() {
}

export default {
  name: 'UiFormItem',

  mixins: [emitter],

  provide() {
    return {
      uiFormItem: this,
    }
  },

  inject: {
    uiForm: {
      default: '',
    },
  },

  // language=HTML (for PhpStorm)
  template: `
    <div class="ui-form-item" :class="{
        'is-error': validateState === 'error',
        'is-validating': validateState === 'validating',
        'is-success': validateState === 'success',
        'is-required': required
      }">
      <div :class="innerItemClass">
        <div v-if="hasLabel" :class="labelClass">
          <label :for="computedId">
            <slot name="label">{{ label }}</slot>
          </label>
        </div>
        <div :class="innerWrapClass">
          <slot></slot>
          <template v-if="$slots.help">
            <div class="help-text">
              <slot name="help"></slot>
            </div>
          </template>
          <div v-if="validateState === 'error'" class="ui-form-item__error">
            <span class="error">{{ validateMessage }}</span>
          </div>
        </div>
      </div>
    </div>
  `,

  props: {
    prop: String,
    id: String,
    label: String,
    labelPosition: String,
    labelColumns: Number,
    inputColumns: Number,
    required: {
      type: Boolean,
      default: undefined,
    },
    rules: Array,
  },

  data() {
    return {
      validateState: '',
      validateMessage: '',
      validator: {},
    }
  },

  computed: {
    hasLabel() {
      return !!(this.label || this.$slots.label)
    },

    computedId() {
      if (this.id) {
        return this.id
      }

      return this.prop ? this.propToId(this.prop) : uuid.v1()
    },

    computedLabelPosition() {
      if (this.labelPosition) {
        return this.labelPosition
      }

      if (!this.uiForm) {
        return 'top'
      }

      return this.uiForm.labelPosition
    },

    innerItemClass() {
      const classes = ['ui-form-item__inner']
      if (this.computedLabelPosition !== 'top') {
        classes.push('row flush')
      }
      return classes
    },

    labelClass() {
      const classes = ['ui-form-item__label']

      if (this.hasLabel && this.computedLabelPosition === 'left') {
        let columns = this.labelColumns
        if (!columns && this.uiForm) {
          // eslint-disable-next-line prefer-destructuring
          columns = this.uiForm.labelColumns
        }

        classes.push('small-12', `large-${columns}`, 'columns')
      }

      return classes
    },

    innerWrapClass() {
      const classes = ['ui-form-item__field']

      if (this.computedLabelPosition === 'left') {
        if (this.hasLabel) {
          let columns = this.inputColumns
          if (!columns && this.uiForm) {
            // eslint-disable-next-line prefer-destructuring
            columns = this.uiForm.inputColumns
          }

          classes.push('small-12', `large-${columns}`, 'columns')
        } else {
          classes.push('small-12', 'columns')
        }
      }

      return classes
    },

    fieldValue() {
      const model = this.uiForm ? this.uiForm.model : null

      if (!model || !this.prop) {
        return null
      }

      return get(model, this.prop)
    },
  },

  watch: {
    error: {
      immediate: true,
      handler(value) {
        this.validateMessage = value
        this.validateState = value ? 'error' : ''
      },
    },
  },

  methods: {
    propToId(prop) {
      return String(prop).replace(/\./g, '_')
    },

    validate(callback) {
      if (typeof callback !== 'function') {
        // eslint-disable-next-line no-param-reassign
        callback = noop
      }

      const rules = this.getRules()

      if ((!rules || rules.length === 0) && this.required === undefined) {
        callback()
        return
      }

      this.validateState = 'validating'

      const descriptor = {}
      descriptor[this.prop] = rules

      const validator = new AsyncValidator(descriptor)
      const model = {}
      model[this.prop] = this.fieldValue

      validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
        this.validateState = !errors ? 'success' : 'error'
        this.validateMessage = errors ? errors[0].message : ''
        callback(this.validateMessage, invalidFields)
        if (this.uiForm) {
          this.uiForm.$emit('validate', this.prop, !errors)
        }
      })
    },

    clearValidate() {
      this.validateState = ''
      this.validateMessage = ''
    },

    getRules() {
      const allFormRules = this.uiForm ? this.uiForm.rules : {}
      const formRules = get(allFormRules, this.prop || 'fdjdfhdjfh', [])
      const requiredRule = this.required !== undefined ? { required: !!this.required } : []

      return [].concat(this.rules || formRules || []).concat(requiredRule)
    },

    onFieldBlur() {
      this.validate()
    },

    onFieldChange() {
      this.validate()
    },
  },

  mounted() {
    if (this.prop) {
      this.dispatch('UiForm', 'ui.form.addField', [this])

      let initialValue = this.fieldValue
      if (Array.isArray(initialValue)) {
        initialValue = [].concat(initialValue)
      }
      Object.defineProperty(this, 'initialValue', { value: initialValue })

      const rules = this.getRules()
      if (rules.length) {
        this.$on('ui.form.blur', this.onFieldBlur)
        this.$on('ui.form.change', this.onFieldChange)
      }
    }
  },

  beforeDestroy() {
    this.dispatch('UiForm', 'ui.form.removeField', [this])
  },
}

